import { IS_EVIDENCE_DOC_EXPORT_SUGGESTIONS_ENABLED } from '@/app-shell/flags/evidence-flags';
import { pendingChatMessageAtom } from '@/atoms/chatLaunchAtoms';
import {
  activeSidebarChatIdAtom,
  sidebarChatIdAtomFamily,
} from '@/atoms/chatSidebarAtoms';
import { docExportBannerClosedConsultSessionIdsAtom } from '@/atoms/floatingAskHeidiAtoms';
import { useQueryDocumentExportSuggestions } from '@/hooks/query/useQueryDocumentExportSuggestions';
import { useQueryDocumentsBySessionId } from '@/hooks/query/useQueryDocumentsBySessionId';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useOpenEvidencePanel } from '@/hooks/useOpenEvidencePanel';
import { track } from '@/lib/analytics/track';
import { isModalClosed } from '@/lib/modalClosedRules';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { useManualExposure } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';
import { documentsGeneratedInSessionAtom } from '@heidi/session/data';
import { useMutateMarkModalAsViewed } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

export type DocExportSuggestionBannerFormat = 'word' | 'pdf';

const DOC_EXPORT_BANNER_MODAL_ID = ModalId.EVIDENCE_DOCUMENT_EXPORT_BANNER;

export type UseDocExportSuggestionBannerOptions = {
  /**
   * When false, skips manual exposure and modal impression side effects so a
   * visibility-only caller does not double-count alongside the banner component.
   */
  trackImpressions?: boolean;
};

export interface DocExportSuggestionBannerView {
  headline: string;
  subcopy: string;
  isBannerHovered: boolean;
  hoveredCta: DocExportSuggestionBannerFormat | null;
  hoverBanner: () => void;
  unhoverBanner: () => void;
  hoverWord: () => void;
  hoverPdf: () => void;
  clearCtaHover: () => void;
  handleClickWord: () => void;
  handleClickPdf: () => void;
  handleDismiss: () => void;
  dismissAriaLabel: string;
}

export function useDocExportSuggestionBanner(
  options: UseDocExportSuggestionBannerOptions = {}
): DocExportSuggestionBannerView | null {
  const { trackImpressions = true } = options;
  const intl = useIntl();
  const sessionId = useCurrentSessionId();
  const isFlagEnabled = IS_EVIDENCE_DOC_EXPORT_SUGGESTIONS_ENABLED();

  const { data: documentsData } = useQueryDocumentsBySessionId(sessionId ?? '');
  const documentsGeneratedInSession = useAtomValue(documentsGeneratedInSessionAtom);

  // Only consider documents that finished generating in this browser session
  // so the banner doesn't show for old sessions reopened from history.
  const firstDocument = useMemo(() => {
    const docs = documentsData?.documents;
    if (!docs?.length) return null;
    const generatedThisSession = docs.filter((doc) =>
      doc.id ? documentsGeneratedInSession.has(doc.id) : false
    );
    if (!generatedThisSession.length) return null;
    const sorted = [...generatedThisSession].sort(
      (a, b) => (a.index ?? 0) - (b.index ?? 0)
    );
    return sorted[0] ?? null;
  }, [documentsData?.documents, documentsGeneratedInSession]);
  const firstDocumentId = firstDocument?.id ?? null;

  const { data: exportSuggestionResponse } = useQueryDocumentExportSuggestions({
    sessionId: sessionId ?? '',
    documentId: firstDocumentId,
    enabled: Boolean(sessionId && firstDocumentId),
  });

  const openEvidencePanel = useOpenEvidencePanel();
  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);
  const setSidebarChatId = useSetAtom(sidebarChatIdAtomFamily(sessionId ?? ''));
  const setPendingMessage = useSetAtom(pendingChatMessageAtom);

  const closedConsultSessionIds = useAtomValue(
    docExportBannerClosedConsultSessionIdsAtom
  );
  const setClosedConsultSessionIds = useSetAtom(
    docExportBannerClosedConsultSessionIdsAtom
  );
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();

  // Backend `seen_modals` record drives the lifetime suppression rule
  // (`DOCUMENT_EXPORT_BANNER_DISMISS_AT_LEAST` in `modalClosedRules.ts`) so the cap survives reloads
  // and follows the user across devices. `useMutateMarkModalAsViewed` patches
  // this view optimistically in `onMutate`, so `isGloballySuppressedByDismissals`
  // flips the moment the 3rd dismiss is recorded — no extra optimistic atom
  // write needed here, and an early one would hide the banner after a single
  // dismiss instead of honouring the configured threshold.
  const { data: userData } = useQueryUser((user) =>
    user ? { modalView: user.seen_modals?.[DOC_EXPORT_BANNER_MODAL_ID] } : undefined
  );
  const isUserHydrated = userData !== undefined;
  const isGloballySuppressedByDismissals = isModalClosed(
    DOC_EXPORT_BANNER_MODAL_ID,
    userData?.modalView
  );

  const bannerClosedByPersistence =
    !sessionId ||
    !isUserHydrated ||
    isGloballySuppressedByDismissals ||
    closedConsultSessionIds.includes(sessionId);

  const closeBannerForConsult = useCallback(() => {
    if (!sessionId) return;
    setClosedConsultSessionIds((prev) =>
      prev.includes(sessionId) ? prev : [...prev, sessionId]
    );
  }, [sessionId, setClosedConsultSessionIds]);

  // Persists action on the backend so the lifetime cap (and engage_count
  // resets) survive reloads and sync across devices. The mutation's `onMutate`
  // patches the user query cache, so `isGloballySuppressedByDismissals` reacts
  // immediately when the dismiss threshold is reached. Per-consult immediate
  // hide is handled by `closeBannerForConsult`.
  const markSeen = useCallback(
    (action: ModalViewAction) => {
      markModalAsViewed({
        modal_id: DOC_EXPORT_BANNER_MODAL_ID,
        increment_count: true,
        action,
      });
    },
    [markModalAsViewed]
  );

  const [isBannerHovered, setIsBannerHovered] = useState(false);
  const [hoveredCta, setHoveredCta] = useState<DocExportSuggestionBannerFormat | null>(
    null
  );

  const apiSuggestion = exportSuggestionResponse?.suggestion ?? null;
  const suggestionCategory = apiSuggestion?.category ?? null;

  const handleOpenEvidence = useCallback(
    (format: DocExportSuggestionBannerFormat) => {
      const documentName =
        firstDocument?.name?.trim() ||
        intl.formatMessage({
          defaultMessage: 'this document',
          id: '9xdI6j5OsT',
        });
      const formatLabel =
        format === 'word'
          ? intl.formatMessage({ defaultMessage: 'Word document', id: 'UCmVsewBrT' })
          : intl.formatMessage({ defaultMessage: 'PDF', id: 'tLuBYBB0JO' });
      const message = intl.formatMessage(
        {
          defaultMessage: 'Export {documentName} to a {format}',
          id: 'RboeaE/2Em',
        },
        { documentName, format: formatLabel }
      );

      setActiveSidebarChatId(null);
      setSidebarChatId(null);
      setPendingMessage(message);
      openEvidencePanel();
    },
    [
      firstDocument,
      intl,
      openEvidencePanel,
      setActiveSidebarChatId,
      setPendingMessage,
      setSidebarChatId,
    ]
  );

  const trackButtonClick = useCallback(
    (action: ButtonAction, label: string) => {
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          screen_name: ScreenName.DocExportSuggestionBanner,
          action,
          label,
          session_id: sessionId ?? undefined,
          ...(suggestionCategory ? { category: suggestionCategory } : {}),
        },
      });
    },
    [sessionId, suggestionCategory]
  );

  const handleClickWord = useCallback(() => {
    closeBannerForConsult();
    markSeen(ModalViewAction.engage);
    trackButtonClick(ButtonAction.DocExportBannerCreateWordDoc, 'create_word_doc');
    handleOpenEvidence('word');
  }, [closeBannerForConsult, handleOpenEvidence, markSeen, trackButtonClick]);

  const handleClickPdf = useCallback(() => {
    closeBannerForConsult();
    markSeen(ModalViewAction.engage);
    trackButtonClick(ButtonAction.DocExportBannerCreatePdf, 'create_pdf');
    handleOpenEvidence('pdf');
  }, [closeBannerForConsult, handleOpenEvidence, markSeen, trackButtonClick]);

  const handleDismiss = useCallback(() => {
    trackButtonClick(ButtonAction.DocExportBannerDismiss, 'dismiss');
    closeBannerForConsult();
    markSeen(ModalViewAction.dismiss);
  }, [closeBannerForConsult, markSeen, trackButtonClick]);

  const hoverBanner = useCallback(() => {
    setIsBannerHovered(true);
  }, []);

  // Leaving the banner also implies leaving any CTA, so clear both at once
  // in case the cursor exits straight off a button onto empty space.
  const unhoverBanner = useCallback(() => {
    setIsBannerHovered(false);
    setHoveredCta(null);
  }, []);

  const hoverWord = useCallback(() => {
    setHoveredCta('word');
  }, []);

  const hoverPdf = useCallback(() => {
    setHoveredCta('pdf');
  }, []);

  const clearCtaHover = useCallback(() => {
    setHoveredCta(null);
  }, []);

  // Visibility signal for `useManualExposure` and `modal_viewed` impression tracking.
  // The API returns `suggestion: null` when the experiment is off or no CTA is
  // appropriate for this document, in which case we don't show the banner at all.
  const isBannerVisible = Boolean(
    sessionId && firstDocumentId && !bannerClosedByPersistence && apiSuggestion
  );

  useManualExposure({
    flagKey: 'release-evidence-doc-export-suggestions',
    isEnabled: isFlagEnabled,
    isVisible: trackImpressions && isBannerVisible,
  });

  // Fire once per mount. Increments `view_count` (without an action so
  // engage/dismiss counters are untouched) so `modalClosedRules` can
  // auto-suppress after the configured impression-without-engagement cap.
  const hasTrackedShownRef = useRef(false);
  useEffect(() => {
    if (!trackImpressions) return;
    if (!isBannerVisible || hasTrackedShownRef.current) return;
    hasTrackedShownRef.current = true;
    markModalAsViewed({
      modal_id: DOC_EXPORT_BANNER_MODAL_ID,
      increment_count: true,
    });
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: { screen_name: ScreenName.DocExportSuggestionBanner },
    });
  }, [isBannerVisible, markModalAsViewed, trackImpressions]);

  if (!isBannerVisible || !apiSuggestion) {
    return null;
  }

  const dismissAriaLabel = intl.formatMessage({
    defaultMessage: 'Dismiss',
    id: 'TDaF6JVgG6',
  });

  return {
    headline: apiSuggestion.headline,
    subcopy: apiSuggestion.subcopy,
    isBannerHovered,
    hoveredCta,
    hoverBanner,
    unhoverBanner,
    hoverWord,
    hoverPdf,
    clearCtaHover,
    handleClickWord,
    handleClickPdf,
    handleDismiss,
    dismissAriaLabel,
  };
}
